import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ViewChild,
  ViewContainerRef,
  type AfterViewInit,
  type ComponentRef,
  type OnInit,
  type Type,
  ChangeDetectionStrategy
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import type { FieldTree } from "@angular/forms/signals";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";

export interface SignalFormInstance {
  rootForm: FieldTree<any>; // The result of the form() function
}

export interface DialogData {
  title: string;
  submitLabel: string;
  closeLabel: string;
  formComponent: Type<any>;
  formInput: any;
  otherInputs?: Record<string, any>;
  disabled: boolean;
}

@Component({
  selector: "lib-signal-form-dialog-component",
  imports: [MatDialogModule, MatButtonModule],
  templateUrl: "./signal-form-dialog-component.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./signal-form-dialog-component.component.css",
})
export class SignalFormDialogComponent implements OnInit, AfterViewInit {
  #destroyRef = inject(DestroyRef);

  @ViewChild("formContainer", { read: ViewContainerRef, static: false }) vcr!: ViewContainerRef;

  dialogData = inject<DialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<SignalFormDialogComponent>);
  #matDialog = inject(MatDialog); // For opening confirmation dialog

  componentRef!: ComponentRef<SignalFormInstance>;

  $componentRef = signal<ComponentRef<SignalFormInstance> | null>(null);

  #cdr = inject(ChangeDetectorRef);

  $isInvalid = computed(() => {
    const ref = this.$componentRef();
    if (!ref) return true;
    const form = ref?.instance?.rootForm();
    // Access the signal value by calling the signal itself
    return form?.invalid() ?? true;
  });

  $isDirty = computed(() => {
    const ref = this.$componentRef();
    if (!ref) return true;
    const form = ref?.instance?.rootForm();
    return form?.dirty() ?? false;
  });

  ngOnInit() {
    // Manage the Backdrop Click
    this.dialogRef.disableClose = true;

    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.handleCancel());
  }

  ngAfterViewInit() {
    const ref = this.vcr.createComponent(this.dialogData.formComponent);

    if (this.dialogData.formInput) {
      ref.setInput("input", this.dialogData.formInput);
    }

    if (this.dialogData.otherInputs) {
      Object.entries(this.dialogData.otherInputs).forEach(([key, value]) => {
        ref.setInput(key, value);
      });
    }

    ref.changeDetectorRef.detectChanges();

    this.$componentRef.set(ref);

    setTimeout(() => {
      this.#cdr.detectChanges();
    }, 0);
  }

  handleCancel() {
    if (this.$isDirty()) {
      const confirmRef = this.#matDialog.open(ConfirmDiscardDialog, { width: "350px" });
      confirmRef.afterClosed().subscribe((discard) => {
        if (discard) this.dialogRef.close();
      });
    } else {
      this.dialogRef.close();
    }
  }

  handleSave() {
    const ref = this.$componentRef();
    if (!ref) return;
    const form = ref?.instance?.rootForm();
    // Check the signal value and then get the data value
    if (form && !form.invalid()) {
      const formValue = form.value(); // value is also a signal
      this.dialogRef.close(formValue);
    }
  }
}

/**
 * Form Dialog Confirm Discard Dialog
 */
@Component({
  selector: "lib-signal-confirm-discard-dialog",
  imports: [MatDialogModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>Discard Changes?</h2>
    <mat-dialog-content> You have unsaved changes. If you leave now, your progress will be lost. </mat-dialog-content>
    <mat-dialog-actions>
      <button
        mat-button
        [mat-dialog-close]="false"
      >
        Keep Editing
      </button>
      <button
        mat-raised-button
        color="warn"
        [mat-dialog-close]="true"
      >
        Discard
      </button>
    </mat-dialog-actions>
  `,
})
export class ConfirmDiscardDialog {}
